import type { ProgramFixedContentRowUpsert } from '../types/domain';
import {
  applyLearningContentOverrides,
  mergeProgramLearningContentFields,
  parseProgramLearningContentOverridesFromEditor,
  parseProgramLearningContentFieldsFromEditor,
  readProgramLearningContentFields,
  type ProgramLearningContentRowLike,
  writeProgramLearningContentFields,
} from './programLearningContentFields';
import { isPristineMinimumProgramContentPlaceholderRows } from './programWorkspaceBaselineDigest';

/** 워크스페이스 patch 병합 — learning_text 본문·ref는 patch, 생략된 note·빈 본문 필드는 baseline에서 보존. @see PROGRAM_LEARNING_ATTACHMENT_INVARIANTS */

const WORKSPACE_PATCH_PRESERVE_TEXT_FIELDS = [
  'timetable_text',
  'unit_text',
  'page_text',
  'assistant_page_text',
  'current_period_text',
  'total_period_text',
  'material_text',
  'record_cross_curricular_text',
  'record_memo_text',
  'record_evaluation_text',
  'record_class_text',
] as const;

type MergeableBaselineRow = {
  order_index: number;
  source_annual_cell_key?: string | null;
  id?: string;
};

/** 슬라이드·활동지 초안 등 learning_text만 갱신된 patch가 단원·쪽수 등 기존 본문을 비우지 않도록 한다 */
export function coalesceSparseProgramWorkspacePatchFromBaseline<
  T extends MergeableBaselineRow & Partial<Record<(typeof WORKSPACE_PATCH_PRESERVE_TEXT_FIELDS)[number], string | null | undefined>>,
>(
  patch: ProgramFixedContentRowUpsert,
  baseline: T
): ProgramFixedContentRowUpsert {
  const next: ProgramFixedContentRowUpsert = { ...patch };
  for (const field of WORKSPACE_PATCH_PRESERVE_TEXT_FIELDS) {
    // null은 잘라내기로 의도적으로 비운 것이므로 복원하지 않음
    if (patch[field] === null) continue;
    const patchVal = String(patch[field] ?? '').trim();
    const baseVal = String(baseline[field] ?? '').trim();
    if (!patchVal && baseVal) {
      next[field] = baseline[field] ?? '';
    }
  }
  const patchMember = patch.record_member_group_id ?? null;
  const baseMember =
    (baseline as { record_member_group_id?: string | null | undefined }).record_member_group_id ?? null;
  if (!patchMember && baseMember) {
    next.record_member_group_id = baseMember;
  }
  return next;
}

/**
 * 행 추가·삭제·통합·이동(`persistKind: replace`)용 — sparse 보존 없이 표 스냅샷을 그대로 반영.
 * (한 달 전 `{ ...b, ...w }`와 동일. coalesce는 칸 편집 patch 전용.)
 */
function mergeWorkspaceRowForReplaceSnapshot<T extends MergeableBaselineRow>(
  base: T,
  patch: ProgramFixedContentRowUpsert
): T {
  return { ...base, ...patch, id: String((patch.id ?? base.id) ?? '') } as T;
}

/** 칸 편집 patch 전용 — 빈 단원·쪽수 등이 learning_text만 갱신된 patch에 의해 지워지지 않게 baseline에서 보존 */
export function mergeWorkspaceRowLearningTextAttachments<T extends MergeableBaselineRow>(
  base: T,
  patch: ProgramFixedContentRowUpsert
): T {
  const coalesced = coalesceSparseProgramWorkspacePatchFromBaseline(patch, base);
  const merged = { ...base, ...coalesced, id: String((coalesced.id ?? base.id) ?? '') } as T;
  const baseFields = readProgramLearningContentFields(base as ProgramLearningContentRowLike);
  const patchHasLearning =
    patch.learning_text !== undefined ||
    patch.learning_note !== undefined ||
    patch.presentation_ref_id !== undefined ||
    patch.worksheet_ref_id !== undefined;
  if (patchHasLearning) {
    if (patch.learning_text === '' || patch.learning_text === null) {
      const cleared = writeProgramLearningContentFields({
        body: '',
        note: '',
        presentationRefId: null,
        worksheetRefId: null,
      });
      Object.assign(merged, cleared);
    } else {
      const patchHasStructuredFields =
        patch.learning_note !== undefined ||
        patch.presentation_ref_id !== undefined ||
        patch.worksheet_ref_id !== undefined;
      const mergedFields = patchHasStructuredFields
        ? applyLearningContentOverrides(baseFields, {
            ...(patch.learning_text !== undefined
              ? { body: String(patch.learning_text ?? '').trim() }
              : {}),
            ...(patch.learning_note !== undefined
              ? { note: String(patch.learning_note ?? '').trim() }
              : {}),
            ...(patch.presentation_ref_id !== undefined
              ? { presentationRefId: patch.presentation_ref_id ?? null }
              : {}),
            ...(patch.worksheet_ref_id !== undefined
              ? { worksheetRefId: patch.worksheet_ref_id ?? null }
              : {}),
          })
        : patch.learning_text !== undefined
          ? applyLearningContentOverrides(
              baseFields,
              parseProgramLearningContentOverridesFromEditor(patch.learning_text)
            )
          : mergeProgramLearningContentFields(baseFields, readProgramLearningContentFields(patch));
      Object.assign(merged, writeProgramLearningContentFields(mergedFields));
    }
  }
  return merged;
}

export type ProgramWorkspaceDisplayMergeOptions = {
  /**
   * `replace`: 표에서 온 전량 스냅샷(삭제·통합 등). baseline보다 짧으면 **남는 baseline 행을 버리고**
   * 워크스페이스 길이만 반환해 UI에 삭제가 반영된다.
   * `patch`(기본): 변경 행만 올린 축약본 — baseline 전체를 유지하고 매칭되는 행만 덮어쓴다.
   */
  persistKind?: 'patch' | 'replace';
  /**
   * 일부 행만 있는 patch를 baseline 어느 행에 덮을지.
   * - `source-key`(기본, 고정 프로그램): 연간 셀 키 → order_index
   * - `id-position`(편제·프로젝트): 행 id → baseline 정렬 위치(i번째)
   */
  identity?: 'source-key' | 'id-position';
};

/**
 * 워크스페이스에 **일부 행만** 있을 때(dirty 축약 저장·patch) UI는 서버 baseline 전체를 유지하고 해당 행만 덮어씀.
 * 워크스페이스 행 수가 baseline 이상이면 전량 스냅샷으로 보고 인덱스 기준으로 병합(기존 동작에 가깝게).
 * `persistKind === 'replace'` 이고 워크스페이스가 더 짧으면 patch 분기 대신 인덱스 병합만 하고 길이는 워크스페이스에 맞춘다.
 * 네트워크 호출 없음 — 이미 메모리에 있는 baseline + localStorage 워크스페이스만 사용.
 */
export function mergeProgramWorkspaceUpsertsIntoBaselineRows<T extends MergeableBaselineRow>(
  baselineSorted: readonly T[],
  workspaceRows: readonly ProgramFixedContentRowUpsert[],
  options?: ProgramWorkspaceDisplayMergeOptions
): T[] {
  if (!workspaceRows.length) return [...baselineSorted];
  const persistKind = options?.persistKind ?? 'patch';
  /** 구조 편집 replace는 sparse coalesce로 빈 칸을 baseline 옛 내용으로 되살리면 표가 안 바뀌어 보인다 */
  const mergeRow =
    persistKind === 'replace' ? mergeWorkspaceRowForReplaceSnapshot : mergeWorkspaceRowLearningTextAttachments;
  if (workspaceRows.length >= baselineSorted.length) {
    return workspaceRows.map((w, i) => {
      const b = baselineSorted[i];
      if (!b) return w as unknown as T;
      return mergeRow(b, w);
    });
  }
  if (persistKind === 'replace') {
    const sortedW = [...workspaceRows].sort(
      (a, b) =>
        a.order_index - b.order_index ||
        String(a.source_annual_cell_key ?? '').localeCompare(String(b.source_annual_cell_key ?? ''))
    );
    if (sortedW.length < baselineSorted.length && isPristineMinimumProgramContentPlaceholderRows(sortedW)) {
      return [...baselineSorted];
    }
    return sortedW.map((w, i) => {
      const b = baselineSorted[i];
      if (!b) return w as unknown as T;
      return mergeRow(b, w);
    });
  }
  if (options?.identity === 'id-position') {
    const byId = new Map<string, ProgramFixedContentRowUpsert>();
    const byPosition = new Map<number, ProgramFixedContentRowUpsert>();
    for (const w of workspaceRows) {
      const id = String(w.id ?? '').trim();
      if (id && baselineSorted.some((b) => String(b.id ?? '').trim() === id)) byId.set(id, w);
      else byPosition.set(w.order_index, w);
    }
    return baselineSorted.map((base, position) => {
      const id = String(base.id ?? '').trim();
      const p = (id ? byId.get(id) : undefined) ?? byPosition.get(position);
      if (!p) return { ...base } as T;
      return mergeWorkspaceRowLearningTextAttachments(base, p);
    });
  }
  const byOrder = new Map<number, ProgramFixedContentRowUpsert>();
  const bySource = new Map<string, ProgramFixedContentRowUpsert>();
  for (const w of workspaceRows) {
    byOrder.set(w.order_index, w);
    const sk = (w.source_annual_cell_key ?? '').trim();
    if (sk) bySource.set(sk, w);
  }
  return baselineSorted.map((base) => {
    const p = bySource.get((base.source_annual_cell_key ?? '').trim()) ?? byOrder.get(base.order_index);
    if (!p) return { ...base } as T;
    return mergeWorkspaceRowLearningTextAttachments(base, p);
  });
}
